/**
 * Pembalut IndexedDB yang kecil dan boleh diuji.
 *
 * Tiada kebergantungan: `fake-indexeddb` dipasang sebagai devDependency untuk ujian, jadi kod
 * yang diuji ialah kod yang berjalan di pelayar.
 */

export const DB_NAME = '72on-pos';
export const DB_VERSION = 2;

export const STORES = {
  meta: 'meta',
  products: 'products',
  variants: 'variants',
  taxRates: 'tax_rates',
  sales: 'sales',
  saleLines: 'sale_lines',
  payments: 'payments',
  outbox: 'outbox',
  shifts: 'shifts',
} as const;

export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request gagal'));
  });
}

export function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction gagal'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction dibatalkan'));
  });
}

export function openDatabase(
  name: string = DB_NAME,
  factory: IDBFactory = indexedDB,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = factory.open(name, DB_VERSION);

    open.onupgradeneeded = () => {
      const db = open.result;

      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta);
      }
      if (!db.objectStoreNames.contains(STORES.products)) {
        db.createObjectStore(STORES.products, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.variants)) {
        const variants = db.createObjectStore(STORES.variants, { keyPath: 'id' });
        variants.createIndex('barcode', 'barcode', { unique: false });
        variants.createIndex('product_id', 'product_id', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.taxRates)) {
        db.createObjectStore(STORES.taxRates, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.sales)) {
        const sales = db.createObjectStore(STORES.sales, { keyPath: 'id' });
        sales.createIndex('client_seq', 'client_seq', { unique: true });
      }
      if (!db.objectStoreNames.contains(STORES.saleLines)) {
        const lines = db.createObjectStore(STORES.saleLines, { keyPath: 'id' });
        lines.createIndex('sale_id', 'sale_id', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.payments)) {
        const payments = db.createObjectStore(STORES.payments, { keyPath: 'id' });
        payments.createIndex('sale_id', 'sale_id', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.shifts)) {
        db.createObjectStore(STORES.shifts, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.outbox)) {
        const outbox = db.createObjectStore(STORES.outbox, { keyPath: 'client_seq' });
        outbox.createIndex('status', 'status', { unique: false });
      }
    };

    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('tidak dapat membuka IndexedDB'));
  });
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = atob(padded);
  // ArrayBuffer eksplisit: API WebCrypto (BufferSource) menolak tatasusunan yang boleh
  // berkongsi memori.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
